import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import {
  Erp1cOperationTypeFormDto,
  type Erp1cOperationTypeFormInterface
} from '@zaoblako/shared/modules/catalog/dtos/erp-1c-operation-type/erp-1c-operation-type-form.dto';
import type { Erp1cOperationTypeFindInterface } from '@zaoblako/shared/modules/catalog/dtos/erp-1c-operation-type/erp-1c-operation-type-find.dto';
import type { Connection1cFindInterface } from '@zaoblako/shared/modules/integration/dtos/connection-1c/connection-1c-find.dto';
import { Integration1cService } from '@zaoblako/web/core/services/integration/integration1c.service';
import { Erp1cOperationTypeService } from '@zaoblako/web/core/services/finance/erp-1c-operation-type.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DirectoryCommonHistoryTypeEnum } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/enums/directory-common-history-type.enum';
import { Erp1cObjectTypeEnum } from '@zaoblako/shared/modules/catalog/enums/erp-1c-object-type.enum';
import { TypeFormEnum } from '@zaoblako/web/shared/components/form-sidebar/enums/type.form.enum';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { DirectoryCommonHistoryComponent } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/directory-common-history.component';
import { OperationTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/operation-type-single-select/operation-type-single-select.component';
import { DatabaseSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/client/database-single-select/database-single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { Erp1cObjectTypeNamePipe } from '@zaoblako/web/shared/pipes/erp-1c-object-type-name.pipe';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TooltipModule } from 'primeng/tooltip';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { TagModule } from 'primeng/tag';

@Component({
  standalone: true,
  selector: 'crm-operation-type-erp-1c-form',
  templateUrl: './operation-type-erp-1c-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    SingleSelectComponent,
    Erp1cObjectTypeNamePipe,
    OperationTypeSingleSelectComponent,
    DatabaseSingleSelectComponent,
    DirectoryCommonHistoryComponent,
    TooltipModule,
    ButtonModule,
    SpinnerComponent,
    TagModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OperationTypeErp1cFormComponent
  extends BaseFormComponent<Erp1cOperationTypeFormInterface>
  implements OnInit
{
  @Input() public readonly index: number;
  public dto = Erp1cOperationTypeFormDto;
  public engine: 'class-transformer' | 'serde' = 'serde';

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public erp1cOperationType = <Erp1cOperationTypeFormInterface>{};

  public objectTypes: Erp1cObjectTypeEnum[] = Object.values(Erp1cObjectTypeEnum);
  public databases: Connection1cFindInterface[] = [];

  public get DirectoryCommonHistoryTypeEnum() {
    return DirectoryCommonHistoryTypeEnum;
  }

  private formSubscription$: Subscription = new Subscription();

  public constructor(
    public readonly erp1cOperationTypeService: Erp1cOperationTypeService,
    private readonly integration1cService: Integration1cService,
    private readonly confirmService: ConfirmDialogService,
    public readonly formStoreService: FormStoreService,
    private readonly errorService: ErrorService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    forkJoin([this.integration1cService.find<Connection1cFindInterface>()]).subscribe(([databases]) => {
      this.databases = databases;
      this.cdRef.markForCheck();
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public ngOnDestroy() {
    this.formSubscription$?.unsubscribe();
  }

  public getById(): void {
    this.formStoreService.setLoading(true, this.index);

    this.erp1cOperationTypeService
      .findById<Erp1cOperationTypeFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (erp1cOperationType) => {
          this.erp1cOperationType = erp1cOperationType;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveErp1cOperationType(): void {
    if (this.checkValidate(this.erp1cOperationType).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.erp1cOperationTypeService
          .update<Erp1cOperationTypeFormInterface, Erp1cOperationTypeFindInterface>(this.erp1cOperationType)
          .subscribe({
            next: (erp1cOperationType) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Вид операции "${this.erp1cOperationType.name}" обновлен!`);
              this.formStoreService.saveNext(erp1cOperationType, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.erp1cOperationTypeService.create<Erp1cOperationTypeFindInterface>(this.erp1cOperationType).subscribe({
          next: (erp1cOperationType) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Вид операции создан!');
            this.formStoreService.saveNext(erp1cOperationType, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteErp1cOperationType(): void {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить вид операции "${this.erp1cOperationType.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.erp1cOperationTypeService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe({
            next: () => {
              this.errorService.addSuccessMessage(`Вид операции "${this.erp1cOperationType.name}" удален!`);
              this.formStoreService.deleteNext(this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }

  public restoreErp1cOperationType(): void {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить вид операции "${this.erp1cOperationType.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.erp1cOperationTypeService
          .restoreOne(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe({
            next: () => {
              this.erp1cOperationType = {
                ...this.erp1cOperationType,
                deleted: null
              } as Erp1cOperationTypeFindInterface;
              this.cdRef.markForCheck();
              this.errorService.addSuccessMessage(`Вид операции "${this.erp1cOperationType.name}" восстановлен!`);
              this.formStoreService.saveNext(this.erp1cOperationType, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }

  public showDialogCreate1C(): void {
    const result = this.formStoreService.addForm<Connection1cFindInterface>(TypeFormEnum.DATABASE_1C, {
      headerText: 'Создание базы 1С',
      widthSize: 'form-sidebar-small',
      isNotShowRedirectInCreate: true
    });

    this.formSubscription$.add(
      result.onSave.subscribe((database) => {
        this.databases.push(database);
        this.databases = [...this.databases];
        this.cdRef.markForCheck();
      })
    );
  }
}
